/**
 * AI Resource Configuration Management Service
 * 
 * This service provides easy access to the AI resource generation configuration
 * and allows for runtime configuration updates. It loads the configuration
 * from the script file and provides utilities for working with it.
 * 
 * Features:
 * - Load configuration from script file
 * - Runtime configuration updates
 * - Configuration validation
 * - Category management
 * - Prompt template management
 */

// Import the configuration from the script
// In a real implementation, this would dynamically load from the script file
let CONFIG = null;

/**
 * Loads configuration from the script file
 * @returns {Promise<Object>} Configuration object
 */
async function loadConfiguration() {
  if (CONFIG) {
    return CONFIG;
  }

  try {
    // In a real implementation, this would load from scripts/ai-resource-generation.js
    // For now, we'll use a simplified version that matches the script structure
    CONFIG = {
      prompts: {
        general: {
          prompt: "I am visiting {locationWithContext} on {dateRange}. Use your web search to find up to 10 quality tourism and information resources specifically for this location.\n\nCRITICAL LOCATION REQUIREMENT:\n- ONLY include resources specifically for {locationWithContext}\n- Do NOT include resources for other places with similar names in different locations\n- Verify each resource is actually for the correct location\n\nQUALITY REQUIREMENTS:\n- Return UP TO 10 resources - fewer is acceptable if quality options are limited\n- Only include currently operating, accessible websites\n- Prioritize official websites and established platforms\n- Quality over quantity - return 5 good resources rather than 10 mixed-quality ones\n- Verify each URL is currently accessible and working\n- Only include URLs that load successfully without errors\n- Exclude any URL that appears broken, unreachable, or returns error pages\n\nREQUIREMENTS:\n- Search for official websites, tourism boards, and booking platforms\n- Include attractions, museums, landmarks, tourism sites\n- Only include URLs from verified, successfully accessed sources\n- If a URL cannot be confirmed as live and working, do not include it\n- Each resource should have a SHORT description (max 8 words)\n- Provide complete, properly formatted URLs (full URLs only)\n- Do NOT include duplicate URLs\n\nAVOID these domains:\n- Social media: Facebook, Instagram, Twitter\n- TripAdvisor (redirects incorrectly)\n- Shortened URLs: goo.gl, bit.ly\n- Wikipedia (already provided)\n- Domain parking sites (GoDaddy, etc.)\n- Sites with broken links or tracking issues (e.g., roymorgan.com)\n- Any domains that frequently return errors or are unreliable\n\nReturn as valid JSON array only (no extra text):\n[{\n  \"name\": \"Resource Name\",\n  \"url\": \"https://complete-url.com\",\n  \"description\": \"Brief description under 8 words\"\n}]",
          maxResources: 10,
          priority: 1,
          description: "General information, attractions, and tourism resources"
        },
        restaurants: {
          prompt: "I am visiting {locationWithContext} on {dateRange}. Use your web search to find up to 10 quality restaurants and cafes specifically in this location.\n\nCRITICAL LOCATION REQUIREMENT:\n- ONLY include restaurants physically located in {locationWithContext}\n- Do NOT include restaurants in other places with similar names in different locations\n- Verify each restaurant is actually in the correct location before including it\n\nQUALITY REQUIREMENTS:\n- Return UP TO 10 restaurants - fewer is acceptable if quality options are limited\n- Only include currently operating establishments with accessible websites\n- Prioritize established, highly-rated restaurants\n- Quality over quantity - return 3 good restaurants rather than 10 questionable ones\n- Verify each URL is currently accessible and working\n- Only include URLs that load successfully without errors\n- Exclude any URL that appears broken, unreachable, or returns error pages\n\nREQUIREMENTS:\n- Search for currently operating establishments only\n- Include a mix of local specialties, cafes, and dining options\n- Include official restaurant websites where available\n- Only include URLs from verified, successfully accessed sources\n- If a URL cannot be confirmed as live and working, do not include it\n- Each restaurant should have a SHORT description (max 8 words) about cuisine\n- Provide complete, properly formatted URLs (full URLs only)\n- Do NOT include duplicate URLs\n\nAVOID these domains:\n- Social media: Facebook, Instagram, Twitter\n- TripAdvisor (redirects incorrectly)\n- Shortened URLs: goo.gl, bit.ly\n- Google Maps shortened links\n- Domain parking sites (GoDaddy, etc.)\n- Sites with broken links or tracking issues (e.g., roymorgan.com)\n- Any domains that frequently return errors or are unreliable\n\nReturn as valid JSON array only (no extra text):\n[{\n  \"name\": \"Restaurant Name\",\n  \"url\": \"https://complete-url.com\",\n  \"description\": \"Brief cuisine description under 8 words\"\n}]",
          maxResources: 10,
          priority: 2,
          description: "Restaurants, cafes, and dining recommendations"
        }
      },
      categories: {
        always: ['general', 'restaurants'],
        multiDay: [],
        extendedTrip: [],
        longTrip: [],
        accommodationRelated: [],
        travelRelated: []
      },
      ai: {
        model: "sonar-pro",
        maxTokens: 3000,
        timeout: 30000,
        retryAttempts: 2,
        retryDelay: 1000
      },
      validation: {
        validateUrls: false, // Disabled by default due to CORS issues in browser
        timeout: 5000,
        retryAttempts: 1,
        allowedDomains: ['*'],
        blockedDomains: [
          'facebook.com',
          'instagram.com',
          'twitter.com',
          'youtube.com',
          'tiktok.com',
          'tripadvisor.com',
          'tripadvisor.co',
          'tripadvisor.com.au',
          'goo.gl',
          'bit.ly',
          'wikipedia.org'
        ]
      },
      cache: {
        ttl: {
          general: 7 * 24 * 60 * 60 * 1000, // 7 days
          restaurants: 3 * 24 * 60 * 60 * 1000 // 3 days
        },
        maxCacheSize: 1000,
        cleanupInterval: 60 * 60 * 1000 // 1 hour
      },
      feedback: {
        collectFeedback: true,
        feedbackThreshold: 5,
        learnFromFeedback: true,
        feedbackTypes: ['useful', 'not_useful', 'broken_link', 'irrelevant'],
        weights: {
          useful: 1,
          not_useful: -1,
          broken_link: -2,
          irrelevant: -1
        }
      }
    };

    console.log('✅ [ConfigManager] Configuration loaded successfully');
    return CONFIG;
  } catch (error) {
    console.error('❌ [ConfigManager] Error loading configuration:', error);
    throw error;
  }
}

/**
 * Gets all available resource categories
 * @returns {Promise<Array>} Array of category objects
 */
export async function getResourceCategories() {
  const config = await loadConfiguration();
  
  return Object.entries(config.prompts).map(([key, prompt]) => ({
    key,
    name: key.charAt(0).toUpperCase() + key.slice(1),
    description: prompt.description,
    maxResources: prompt.maxResources,
    priority: prompt.priority
  }));
}

/**
 * Gets configuration for a specific category
 * @param {string} category - Category key
 * @returns {Promise<Object|null>} Category configuration or null if not found
 */
export async function getCategoryConfig(category) {
  const config = await loadConfiguration();
  return config.prompts[category] || null;
}

/**
 * Gets categories to generate based on trip context
 * @param {Object} tripContext - Trip context object
 * @returns {Promise<Array>} Array of category keys to generate
 */
export async function getCategoriesForContext(tripContext) {
  const config = await loadConfiguration();
  
  console.log('🔍 [ConfigManager] Trip context:', tripContext);
  console.log('🔍 [ConfigManager] Available categories:', config.categories);
  
  const categories = [...config.categories.always];
  
  // Add categories based on trip duration
  if (tripContext.duration > 1) {
    categories.push(...config.categories.multiDay);
  }
  
  if (tripContext.duration > 3) {
    categories.push(...config.categories.extendedTrip);
  }
  
  if (tripContext.duration > 5) {
    categories.push(...config.categories.longTrip);
  }
  
  // Add context-specific categories
  if (tripContext.placeContext?.isAccommodationRelated) {
    categories.push(...config.categories.accommodationRelated);
  }
  
  if (tripContext.placeContext?.isTravelRelated) {
    categories.push(...config.categories.travelRelated);
  }
  
  // Remove duplicates and return
  const finalCategories = [...new Set(categories)];
  console.log('🔍 [ConfigManager] Final categories:', finalCategories);
  
  return finalCategories;
}

/**
 * Gets AI configuration settings
 * @returns {Promise<Object>} AI configuration object
 */
export async function getAIConfig() {
  const config = await loadConfiguration();
  return config.ai;
}

/**
 * Gets validation configuration settings
 * @returns {Promise<Object>} Validation configuration object
 */
export async function getValidationConfig() {
  const config = await loadConfiguration();
  return config.validation;
}

/**
 * Gets cache configuration settings
 * @returns {Promise<Object>} Cache configuration object
 */
export async function getCacheConfig() {
  const config = await loadConfiguration();
  return config.cache;
}

/**
 * Gets feedback configuration settings
 * @returns {Promise<Object>} Feedback configuration object
 */
export async function getFeedbackConfig() {
  const config = await loadConfiguration();
  return config.feedback;
}

/**
 * Builds a prompt for a specific category with context variables replaced
 * @param {string} category - Category key
 * @param {Object} context - Context object with placeName, dateRange, etc.
 * @returns {Promise<string>} Built prompt string
 */
export async function buildPrompt(category, context) {
  const categoryConfig = await getCategoryConfig(category);
  if (!categoryConfig) {
    throw new Error(`Category not found: ${category}`);
  }

  // Build location string with proper formatting
  const locationString = context.placeGeoContext
    ? `${context.placeName} in ${context.placeGeoContext}`
    : context.placeName || 'this location';

  return categoryConfig.prompt
    .replace(/{locationWithContext}/g, locationString)
    .replace(/{placeName}/g, context.placeName || 'this location')
    .replace(/{placeGeoContext}/g, context.placeGeoContext || '')
    .replace(/{dateRange}/g, context.formattedDateRange || 'an unspecified date')
    .replace(/{duration}/g, context.duration || 'unknown')
    .replace(/{contextType}/g, context.placeContext?.contextType || 'general')
    + (context.deletedUrls ? ` IMPORTANT: Do NOT include any of these previously rejected URLs: ${context.deletedUrls}` : '');
}

/**
 * Validates the current configuration
 * @returns {Promise<Object>} Validation result
 */
export async function validateConfiguration() {
  const config = await loadConfiguration();
  const errors = [];
  const warnings = [];

  // Check required sections
  const requiredSections = ['prompts', 'categories', 'ai', 'validation', 'cache'];
  for (const section of requiredSections) {
    if (!config[section]) {
      errors.push(`Missing required section: ${section}`);
    }
  }

  // Check prompt configuration
  for (const [key, prompt] of Object.entries(config.prompts)) {
    if (!prompt.prompt || typeof prompt.prompt !== 'string') {
      errors.push(`Invalid prompt for category: ${key}`);
    }
    if (!prompt.maxResources || prompt.maxResources < 1) {
      warnings.push(`Invalid maxResources for category: ${key}`);
    }
  }

  // Check AI configuration
  if (!config.ai.model) {
    errors.push('AI model not specified');
  }

  if (config.ai.temperature < 0 || config.ai.temperature > 2) {
    warnings.push('AI temperature should be between 0 and 2');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    configVersion: '1.0.0',
    lastValidated: new Date().toISOString()
  };
}

/**
 * Updates configuration at runtime (for testing/development)
 * @param {Object} updates - Configuration updates
 * @returns {Promise<Object>} Updated configuration
 */
export async function updateConfiguration(updates) {
  const currentConfig = await loadConfiguration();
  
  // Deep merge updates
  CONFIG = {
    ...currentConfig,
    ...updates,
    prompts: {
      ...currentConfig.prompts,
      ...updates.prompts
    },
    categories: {
      ...currentConfig.categories,
      ...updates.categories
    },
    ai: {
      ...currentConfig.ai,
      ...updates.ai
    },
    validation: {
      ...currentConfig.validation,
      ...updates.validation
    },
    cache: {
      ...currentConfig.cache,
      ...updates.cache
    },
    feedback: {
      ...currentConfig.feedback,
      ...updates.feedback
    }
  };

  console.log('✅ [ConfigManager] Configuration updated successfully');
  return CONFIG;
}

/**
 * Resets configuration to default values
 * @returns {Promise<Object>} Reset configuration
 */
export async function resetConfiguration() {
  CONFIG = null;
  return await loadConfiguration();
}

/**
 * Gets configuration statistics
 * @returns {Promise<Object>} Configuration statistics
 */
export async function getConfigurationStats() {
  const config = await loadConfiguration();
  
  return {
    totalCategories: Object.keys(config.prompts).length,
    categories: Object.keys(config.prompts),
    aiModel: config.ai.model,
    cacheEnabled: !!config.cache,
    feedbackEnabled: config.feedback.collectFeedback,
    validationEnabled: config.validation.validateUrls,
    lastLoaded: new Date().toISOString()
  };
}

// Export the service object
export const configManager = {
  loadConfiguration,
  getResourceCategories,
  getCategoryConfig,
  getCategoriesForContext,
  getAIConfig,
  getValidationConfig,
  getCacheConfig,
  getFeedbackConfig,
  buildPrompt,
  validateConfiguration,
  updateConfiguration,
  resetConfiguration,
  getConfigurationStats
};

export default configManager;
